import {Component, effect, OnDestroy, OnInit, Signal, TemplateRef, ViewChild, AfterViewInit} from '@angular/core';
import {ProductService} from '../../services/product/product.service';
import {Product} from '../../models/product';
import {StoreService} from '../../services/property/store.service';
import {Store} from '../../models/store';
import {UiToastService} from '../../shared/ui';
import {Household} from '../../models/household';
import {Context} from '../../models/context';
import {faEdit, faBarcode, faFilter} from '@fortawesome/free-solid-svg-icons';
import {UiModalComponent} from '../../shared/ui';
import {ContextService} from "../../services/context/context-signal.service";
import {ActivatedRoute, Params, Router} from '@angular/router';
import {Subject} from 'rxjs';
import {debounceTime, distinctUntilChanged, takeUntil} from 'rxjs/operators';
import {PageHeaderService} from '../../services/page-header/page-header.service';
import {PageAction} from '../../components/app-shell/page-action.model';
import { AppLogger, AppLoggerService } from '../../core/logging';
import {PRODUCT_PLACEHOLDER_ICON} from '../../shared/product-placeholder-icon';
import {CatalogCategory, CatalogProductKind, CatalogService} from '../../services/catalog/catalog.service';
import {UiSelectOption} from '../../shared/ui/select/ui-select.component';

@Component({
  selector: 'app-products',
  templateUrl: './products.component.html',
  styleUrls: ['./products.component.scss'],
  standalone: false
})
export class ProductsComponent implements OnInit, OnDestroy, AfterViewInit {

  private readonly tag = '[Assortment]';
  private readonly log: AppLogger;

  context: Signal<Context> = this.contextService.context;

  products: Product[];
  filteredProducts: Product[] = [];
  pagedProducts: Product[] = [];

  selectedFilter: 'all' | 'inStock' | 'lowStock' | 'outOfStock' = 'all';
  searchTerm = '';
  selectedCategoryKey = '';
  selectedProductKey = '';
  household: Household;

  store: Store;
  stores: Store[];

  categories: CatalogCategory[] = [];
  productKinds: CatalogProductKind[] = [];
  categoryOptions: UiSelectOption[] = [{value: '', label: 'All categories'}];
  productKindOptions: UiSelectOption[] = [{value: '', label: 'All product kinds'}];

  readonly defaultPageSize = 25;
  readonly pageSizeOptions: number[] = [25, 50, 100];
  currentPage = 1;
  pageSize = this.defaultPageSize;
  totalItems = 0;
  totalPages = 0;

  faEdit = faEdit;
  faScan = faBarcode;
  faFilter = faFilter;
  faProductPlaceholder = PRODUCT_PLACEHOLDER_ICON;

  @ViewChild('scannerModal') scannerModal?: UiModalComponent;
  @ViewChild('scanner') scanner?: any;
  @ViewChild('categoryFilterModal') categoryFilterModal?: UiModalComponent;

  private readonly searchInput$ = new Subject<string>();
  private readonly destroy$ = new Subject<void>();

  @ViewChild('toolbarSearch') toolbarSearchTemplate!: TemplateRef<any>;

  constructor(private contextService: ContextService,
              private storeService: StoreService,
              private productService: ProductService,
              private catalogService: CatalogService,
              private appLogger: AppLoggerService,
              public toastService: UiToastService,
              private route: ActivatedRoute,
              private router: Router,
              private pageHeaderService: PageHeaderService) {
    this.log = this.appLogger.createForClass(ProductsComponent);

    effect(() => {
      const c = this.context();
      if (!c?.isSet?.()) return;
      this.household = c.household;
      this.loadProductsForCurrentState();
    });
  }

  ngOnInit(): void {
    this.log.info('Initialize product list');

    const initialParams = this.route.snapshot.queryParamMap;
    const filterParam = (initialParams.get('filter') as 'all' | 'inStock' | 'lowStock' | 'outOfStock') || 'all';
    const searchParam = initialParams.get('search') || '';
    const categoryParam = initialParams.get('category_key') || '';
    const productKeyParam = initialParams.get('product_key') || '';
    const pageParamRaw = initialParams.get('page');
    const pageSizeParamRaw = initialParams.get('pageSize');

    if (filterParam) {
      this.selectedFilter = filterParam;
    }
    if (typeof searchParam === 'string') {
      this.searchTerm = searchParam;
    }
    this.selectedCategoryKey = categoryParam;
    this.selectedProductKey = productKeyParam;

    const parsedPage = pageParamRaw ? Number(pageParamRaw) : NaN;
    const parsedPageSize = pageSizeParamRaw ? Number(pageSizeParamRaw) : NaN;

    if (!isNaN(parsedPage) && parsedPage >= 1) {
      this.currentPage = Math.floor(parsedPage);
    }

    if (!isNaN(parsedPageSize) && this.pageSizeOptions.includes(parsedPageSize)) {
      this.pageSize = parsedPageSize;
    }

    this.route.queryParams.subscribe(params => {
      this.applyQueryParams(params);
    });

    this.searchInput$
      .pipe(
        debounceTime(200),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe(term => {
        this.log.debug('Search term debounced', { term });
        this.searchTerm = term?.trim() ?? '';
        this.currentPage = 1;
        this.loadProductsForCurrentState();
        this.updateQueryParams();
      });

    this.loadCatalogFilters();
  }

  private loadCatalogFilters(): void {
    this.catalogService.getCategories().subscribe({
      next: (cats) => {
        this.categories = cats ?? [];
        this.categoryOptions = [
          {value: '', label: 'All categories'},
          ...this.categories
            .slice()
            .sort((a, b) => (a.name_de || a.key).localeCompare(b.name_de || b.key))
            .map(c => ({value: c.key, label: c.name_de || c.key})),
        ];
      },
      error: (e) => this.log.warn('Failed to load catalog categories', e),
    });

    this.catalogService.getProductKinds().subscribe({
      next: (kinds) => {
        this.productKinds = kinds ?? [];
        this.productKindOptions = [
          {value: '', label: 'All product kinds'},
          ...this.productKinds
            .slice()
            .sort((a, b) => (a.display_name || a.key).localeCompare(b.display_name || b.key))
            .map(k => ({value: k.key, label: k.display_name || k.key})),
        ];
      },
      error: (e) => this.log.warn('Failed to load catalog product kinds', e),
    });
  }

  ngAfterViewInit(): void {
    // Use setTimeout to ensure templates are registered after view is completely stable
    // This prevents them from being cleared during the initial change detection cycle
    setTimeout(() => {
      // Set page title
      this.pageHeaderService.setPageTitle('Products');

      // Optional: show a back button when the caller requests it via query param (e.g. from Shopping page).
      const back = this.route.snapshot.queryParamMap.get('back');
      if (back) {
        this.pageHeaderService.setBackButton({
          label: 'Back',
          preferHistoryBack: true,
          display: 'auto',
        });
      }

      // Inject search template into center zone of toolbar
      if (this.toolbarSearchTemplate) {
        this.pageHeaderService.setSearch(this.toolbarSearchTemplate);
      }

      // Declarative toolbar actions for the new AppShell.
      this.refreshPageActions();
    }, 0);
  }

  /**
   * (Re)publish toolbar actions to the AppShell. The "New" action is pinned
   * so it stays visible as a toolbar button on both mobile and desktop.
   */
  private refreshPageActions(): void {
    const actions: PageAction[] = [
      {
        type: 'button',
        id: 'new',
        label: 'New',
        icon: this.faEdit,
        variant: 'primary',
        pinned: true,
        onClick: () => this.openAddProduct(),
      },
    ];
    this.pageHeaderService.setPageActions(actions);
  }

  ngOnDestroy(): void {
    // Clear both search and actions when navigating away
    this.pageHeaderService.clearAll();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private mapFilterToStockStatus(): 'in' | 'low' | 'out' | undefined {
    switch (this.selectedFilter) {
      case 'inStock':
        return 'in';
      case 'lowStock':
        return 'low';
      case 'outOfStock':
        return 'out';
      default:
        return undefined;
    }
  }

  private classifySearchTerm(term: string): { mode: 'none' | 'text'; value?: string } {
    const value = term?.trim();
    if (!value) {
      return {mode: 'none'};
    }
    return {mode: 'text', value};
  }

  private loadProductsForCurrentState(): void {
    if (!this.household?.id) {
      return;
    }

    const stockStatus = this.mapFilterToStockStatus();
    const {mode, value} = this.classifySearchTerm(this.searchTerm);

    const options: {
      stockStatus?: 'in' | 'low' | 'out';
      query?: string;
      categoryKey?: string;
      productKey?: string;
    } = {};

    if (stockStatus) {
      options.stockStatus = stockStatus;
    }

    if (mode === 'text' && value) {
      options.query = value;
    }

    if (this.selectedCategoryKey) {
      options.categoryKey = this.selectedCategoryKey;
    }

    if (this.selectedProductKey) {
      options.productKey = this.selectedProductKey;
    }

    this.log.debug('Loading products for current state', {
      householdId: this.household.id,
      selectedFilter: this.selectedFilter,
      searchMode: mode,
      searchValue: value,
      options
    });

    this.productService.getAllProducts(this.household.id, options).subscribe(products => {
      this.log.debug('Products loaded', { count: products?.length ?? 0, householdId: this.household.id });
      this.products = products;
      this.filteredProducts = products;
      this.totalItems = products.length;
      this.totalPages = this.pageSize > 0 ? Math.ceil(this.totalItems / this.pageSize) : 0;

      if (this.currentPage < 1) {
        this.currentPage = 1;
      }
      if (this.totalPages > 0 && this.currentPage > this.totalPages) {
        this.currentPage = this.totalPages;
      }

      this.updatePagedProducts();
    });
  }

  private applyQueryParams(params: Params): void {
    const filter = (params['filter'] as 'all' | 'inStock' | 'lowStock' | 'outOfStock') || 'all';
    const search = (params['search'] as string) || '';
    const categoryKey = (params['category_key'] as string) || '';
    const productKey = (params['product_key'] as string) || '';
    const pageParam = params['page'];
    const pageSizeParam = params['pageSize'];

    let changed = false;
    let paginationChanged = false;

    if (filter !== this.selectedFilter) {
      this.selectedFilter = filter;
      changed = true;
    }
    if (search !== this.searchTerm) {
      this.searchTerm = search;
      changed = true;
    }
    if (categoryKey !== this.selectedCategoryKey) {
      this.selectedCategoryKey = categoryKey;
      changed = true;
    }
    if (productKey !== this.selectedProductKey) {
      this.selectedProductKey = productKey;
      changed = true;
    }

    if (pageParam !== undefined) {
      const page = Number(pageParam);
      if (!isNaN(page) && page >= 1 && page !== this.currentPage) {
        this.currentPage = Math.floor(page);
        paginationChanged = true;
      }
    }

    if (pageSizeParam !== undefined) {
      const pageSize = Number(pageSizeParam);
      if (!isNaN(pageSize) && this.pageSizeOptions.includes(pageSize) && pageSize !== this.pageSize) {
        this.pageSize = pageSize;
        paginationChanged = true;
      }
    }

    if (changed) {
      this.currentPage = 1;
      this.loadProductsForCurrentState();
    } else if (paginationChanged) {
      this.updatePagedProducts();
    }
  }

  // Remove client-side filtering logic; keep only pagination over products already filtered by backend
  private applyFilter() {
    this.filteredProducts = this.products || [];
    this.totalItems = this.filteredProducts.length;
    this.totalPages = this.pageSize > 0 ? Math.ceil(this.totalItems / this.pageSize) : 0;

    if (this.currentPage < 1) {
      this.currentPage = 1;
    }
    if (this.totalPages > 0 && this.currentPage > this.totalPages) {
      this.currentPage = this.totalPages;
    }

    this.updatePagedProducts();
  }

  loadProducts(_householdId: number): void {
    this.loadProductsForCurrentState();
  }

  onSearch(term: string) {
    this.searchInput$.next(term ?? '');
  }

  setFilter(filter: 'all' | 'inStock' | 'lowStock' | 'outOfStock') {
    this.selectedFilter = filter;
    this.currentPage = 1;
    this.loadProductsForCurrentState();
    this.updateQueryParams();
  }

  clearSearch(inputRef?: HTMLInputElement) {
    this.searchTerm = '';
    if (inputRef) {
      try {
        (inputRef as any).value = '';
      } catch (e) {
      }
    }
    this.currentPage = 1;
    this.loadProductsForCurrentState();
    this.updateQueryParams();
  }

  private updatePagedProducts(): void {
    if (!this.filteredProducts || this.filteredProducts.length === 0) {
      this.pagedProducts = [];
      return;
    }

    const startIndex = (this.currentPage - 1) * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    this.pagedProducts = this.filteredProducts.slice(startIndex, endIndex);
  }

  private updateQueryParams(): void {
    const queryParams: any = {
      filter: this.selectedFilter !== 'all' ? this.selectedFilter : undefined,
      search: this.searchTerm && this.searchTerm.trim().length > 0 ? this.searchTerm.trim() : undefined,
      category_key: this.selectedCategoryKey ? this.selectedCategoryKey : undefined,
      product_key: this.selectedProductKey ? this.selectedProductKey : undefined,
      page: this.currentPage !== 1 ? this.currentPage : undefined,
      pageSize: this.pageSize !== this.defaultPageSize ? this.pageSize : undefined
    };

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: 'merge',
      replaceUrl: true
    }).then();
  }

  setCategoryFilter(key: string): void {
    const next = key ?? '';
    if (next === this.selectedCategoryKey) return;
    this.selectedCategoryKey = next;
    this.currentPage = 1;
    this.loadProductsForCurrentState();
    this.updateQueryParams();
  }

  openCategoryFilter(): void {
    this.categoryFilterModal?.open();
  }

  closeCategoryFilter(): void {
    this.categoryFilterModal?.close();
  }

  selectCategoryFromModal(key: string): void {
    this.setCategoryFilter(key);
    this.closeCategoryFilter();
  }

  get selectedCategoryLabel(): string {
    if (!this.selectedCategoryKey) return '';
    const match = this.categories.find(c => c.key === this.selectedCategoryKey);
    return match?.name_de || this.selectedCategoryKey;
  }

  setProductKindFilter(key: string): void {
    const next = key ?? '';
    if (next === this.selectedProductKey) return;
    this.selectedProductKey = next;
    this.currentPage = 1;
    this.loadProductsForCurrentState();
    this.updateQueryParams();
  }

  get hasActiveSubFilters(): boolean {
    return !!(this.selectedCategoryKey || this.selectedProductKey);
  }

  /** Product-kind options without the synthetic "All …" entry, used for the autocomplete. */
  get productKindAutocompleteOptions(): UiSelectOption[] {
    return this.productKindOptions.filter(o => o.value !== '');
  }

  clearSubFilters(): void {
    if (!this.hasActiveSubFilters) return;
    this.selectedCategoryKey = '';
    this.selectedProductKey = '';
    this.currentPage = 1;
    this.loadProductsForCurrentState();
    this.updateQueryParams();
  }


  onPageChange(page: number): void {
    if (page < 1 || (this.totalPages > 0 && page > this.totalPages)) {
      return;
    }
    this.currentPage = page;
    this.updatePagedProducts();
    this.updateQueryParams();
  }

  onPageSizeChange(size: number | string): void {
    const parsed = typeof size === 'string' ? Number(size) : size;
    if (!parsed || !this.pageSizeOptions.includes(parsed) || parsed === this.pageSize) {
      return;
    }
    this.pageSize = parsed;
    this.currentPage = 1;
    this.applyFilter();
    this.updateQueryParams();
  }

  trackByProductId(index: number, item: Product): number | string {
    return item?.id ?? index;
  }

  loadStores(householdId: number): void {
    this.storeService.stores(householdId).subscribe(data => {
      this.stores = data;
    });
  }

  openAddProduct(): void {
    this.router.navigate(['/products/new']);
  }

  openScanner(): void {
    this.scannerModal?.open();
    setTimeout(() => {
      this.scanner?.start?.();
    }, 0);
  }

  onScannerClosed(): void {
    this.scanner?.stop?.();
  }

  onScannerCodeAccepted(code: string): void {
    if (!code) return;

    // Feed to existing debounced search pipeline.
    this.onSearch(code);
    this.scanner?.stop?.();
    this.scannerModal?.close();
  }
}
